import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import type { AiModelOption, AiTestRun } from './types';
import { getTesterRun, getTesterRunArtifact } from './api';

interface RunHistoryProps {
  runs: AiTestRun[];
  models: AiModelOption[];
  onRefresh: () => Promise<void> | void;
}

function slugifyFilename(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function downloadJsonFile(value: unknown, filename: string) {
  downloadTextFile(`${JSON.stringify(value, null, 2)}\n`, filename);
}

function buildReportFilename(run: AiTestRun, jobId: string, country?: string): string {
  const parts = [
    'source-of-truth',
    `run-${run.id}`,
    slugifyFilename(run.project_name),
  ];
  if (country) {
    parts.push(slugifyFilename(country));
  } else if (jobId) {
    parts.push(jobId.slice(0, 8));
  }
  return `${parts.filter(Boolean).join('-') || 'report'}.txt`;
}

function buildJtbdFilename(run: AiTestRun, kind: 'statements' | 'step5'): string {
  const ext = kind === 'step5' ? 'json' : 'txt';
  return `jtbd-run-${run.id}-${slugifyFilename(run.project_name) || 'project'}-${kind}.${ext}`;
}

function buildTensionFilename(run: AiTestRun, kind: 'tensions' | 'json'): string {
  const ext = kind === 'json' ? 'json' : 'txt';
  return `tension-run-${run.id}-${slugifyFilename(run.project_name) || 'project'}-${kind}.${ext}`;
}

function buildRecipeFilename(run: AiTestRun, kind: 'options' | 'json'): string {
  const ext = kind === 'json' ? 'json' : 'txt';
  return `recipe-run-${run.id}-${slugifyFilename(run.project_name) || 'project'}-${kind}.${ext}`;
}

function buildWrittenFilename(run: AiTestRun): string {
  return `written-concept-run-${run.id}-${slugifyFilename(run.project_name) || 'project'}.txt`;
}

function buildVisualFilename(run: AiTestRun, mime?: string): string {
  const ext = (mime || '').includes('jpeg') || (mime || '').includes('jpg') ? 'jpg' : 'png';
  return `visual-concept-run-${run.id}-${slugifyFilename(run.project_name) || 'project'}.${ext}`;
}

function isVisualGenerator(key: AiTestRun['generator_key']): boolean {
  return (
    key === 'visual_concept'
    || key === 'visual_concept_regenerate'
    || key === 'visual_concept_edit'
  );
}

function runModelLabel(run: AiTestRun): string {
  const snapshot = run.config_snapshot || {};
  const fromResult = (run.result?.model || '').trim();
  const fromSnapshot = typeof snapshot.model_id === 'string' ? snapshot.model_id.trim() : '';
  const model = fromResult || fromSnapshot;
  const provider = (run.result?.provider || '').trim();
  if (model && provider) return `${model} (${provider})`;
  return model || provider;
}

function visualSrc(imageUrl?: string, imageB64?: string, mimeType?: string): string {
  if (imageUrl) return imageUrl;
  if (imageB64) return `data:${mimeType || 'image/png'};base64,${imageB64}`;
  return '';
}

function downloadPngFromB64(b64: string, mimeType: string, filename: string) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mimeType || 'image/png' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function downloadImageUrl(url: string, filename: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.target = '_blank';
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function tensionText(item: { tension_text?: string; text?: string; tension?: string } | string): string {
  if (typeof item === 'string') return item.trim();
  return (item.tension_text || item.text || item.tension || '').trim();
}

function optionText(item: { option_text?: string } | string): string {
  if (typeof item === 'string') return item.trim();
  return (item.option_text || '').trim();
}

function formatRecipeAttributes(
  attributes: NonNullable<AiTestRun['result']['attributes']>,
): string {
  const lines: string[] = [];
  for (const block of attributes) {
    const name = block.name || 'attribute';
    lines.push(name);
    if (block.error) {
      lines.push(`  error: ${block.error}`);
      continue;
    }
    const options = block.options || {};
    for (const bucket of ['established', 'emerging', 'breakthrough'] as const) {
      const items = options[bucket] || [];
      if (!items.length) continue;
      lines.push(`  ${bucket}:`);
      for (const item of items) {
        const text = optionText(item);
        if (text) lines.push(`    - ${text}`);
      }
    }
  }
  return lines.join('\n');
}

function formatTokenCount(value: unknown): string | null {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n).toLocaleString();
}

function tokenAmount(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parseRate(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function formatUsd(value: number): string {
  return `$${value.toFixed(6).replace(/\.?0+$/, '')}`;
}

function runInputTokens(run: AiTestRun): unknown {
  return run.usage?.input_tokens ?? run.usage?.total_input_tokens;
}

function runOutputTokens(run: AiTestRun): unknown {
  return run.usage?.output_tokens ?? run.usage?.total_output_tokens;
}

function findRunModel(run: AiTestRun, models: AiModelOption[]): AiModelOption | undefined {
  const snapshot = run.config_snapshot || {};
  const fromResult = (run.result?.model || '').trim();
  const fromSnapshot = typeof snapshot.model_id === 'string' ? snapshot.model_id.trim() : '';
  const modelId = fromResult || fromSnapshot;
  if (!modelId) return undefined;
  const provider = (
    run.result?.provider
    || (typeof snapshot.provider === 'string' ? snapshot.provider : '')
  ).trim();
  if (provider) {
    const exact = models.find((model) => model.model_id === modelId && model.provider === provider);
    if (exact) return exact;
  }
  return models.find((model) => model.model_id === modelId);
}

function modelHasRates(model: AiModelOption | undefined): boolean {
  if (!model) return false;
  return parseRate(model.cost_per_1m_input) != null || parseRate(model.cost_per_1m_output) != null;
}

function estimateTokensCost(
  inputTokens: unknown,
  outputTokens: unknown,
  model: AiModelOption | undefined,
): number | null {
  if (!modelHasRates(model)) return null;
  const inp = tokenAmount(inputTokens);
  const out = tokenAmount(outputTokens);
  const inRate = parseRate(model?.cost_per_1m_input);
  const outRate = parseRate(model?.cost_per_1m_output);
  let total = 0;
  if (inRate != null) total += (inp / 1_000_000) * inRate;
  if (outRate != null) total += (out / 1_000_000) * outRate;
  return total;
}

function estimateRunCost(run: AiTestRun, models: AiModelOption[]): number | null {
  const model = findRunModel(run, models);
  if (model) return estimateTokensCost(runInputTokens(run), runOutputTokens(run), model);
  const raw = run.estimated_cost_usd ?? run.cost_usd;
  if (!raw) return null;
  const stored = Number(raw);
  return Number.isFinite(stored) ? stored : null;
}

function usageSummary(run: AiTestRun, models: AiModelOption[]): string | null {
  const input = formatTokenCount(runInputTokens(run));
  const output = formatTokenCount(runOutputTokens(run));
  const cost = estimateRunCost(run, models);
  const parts: string[] = [];
  if (input) parts.push(`${input} in`);
  if (output) parts.push(`${output} out`);
  if (cost != null) parts.push(formatUsd(cost));
  return parts.length ? parts.join(' · ') : null;
}

function TokenUsageBlock({ run, models }: { run: AiTestRun; models: AiModelOption[] }) {
  const rows = stepUsageRows(run);
  const input = formatTokenCount(runInputTokens(run));
  const output = formatTokenCount(runOutputTokens(run));
  const model = findRunModel(run, models);
  const showCost = modelHasRates(model) || (!model && Boolean(run.estimated_cost_usd || run.cost_usd));
  const totalCost = estimateRunCost(run, models);
  if (!rows.length && !input && !output) return null;
  return (
    <div className="rounded-md bg-[#F7F7F7] p-3">
      <p className="text-[12px] font-medium mb-2">Token usage</p>
      {rows.length > 0 ? (
        <table className="w-full text-[11px] text-[#4A4A4A]">
          <thead>
            <tr className="text-left text-[#8A8A8A]">
              <th className="font-medium pr-3 pb-1">Step</th>
              <th className="font-medium pr-3 pb-1">Input</th>
              <th className="font-medium pr-3 pb-1">Output</th>
              {showCost && <th className="font-medium pb-1">Cost</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const stepCost = estimateTokensCost(row.inputRaw, row.outputRaw, model);
              return (
                <tr key={row.step}>
                  <td className="pr-3 py-0.5">{row.step}</td>
                  <td className="pr-3 py-0.5">{row.input}</td>
                  <td className="pr-3 py-0.5">{row.output}</td>
                  {showCost && (
                    <td className="py-0.5">{stepCost != null ? formatUsd(stepCost) : '—'}</td>
                  )}
                </tr>
              );
            })}
            <tr className="font-medium text-[#0C0B0B]">
              <td className="pr-3 pt-1">Total</td>
              <td className="pr-3 pt-1">{input || '0'}</td>
              <td className="pr-3 pt-1">{output || '0'}</td>
              {showCost && (
                <td className="pt-1">{totalCost != null ? formatUsd(totalCost) : '—'}</td>
              )}
            </tr>
          </tbody>
        </table>
      ) : (
        <p className="text-[11px] text-[#4A4A4A]">
          {input || '0'} in · {output || '0'} out
          {totalCost != null ? ` · ${formatUsd(totalCost)}` : ''}
        </p>
      )}
    </div>
  );
}

function stepUsageRows(run: AiTestRun): Array<{
  step: string;
  input: string;
  output: string;
  inputRaw: unknown;
  outputRaw: unknown;
}> {
  const byStep = run.usage?.by_step || run.usage?.by_attribute;
  if (!byStep || typeof byStep !== 'object' || Array.isArray(byStep)) return [];
  return Object.entries(byStep as Record<string, unknown>).map(([step, raw]) => {
    const row = raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
    return {
      step,
      input: formatTokenCount(row.input_tokens) || '0',
      output: formatTokenCount(row.output_tokens) || '0',
      inputRaw: row.input_tokens,
      outputRaw: row.output_tokens,
    };
  });
}

interface JtbdViewState {
  statements: string[];
  intermediate: Record<string, unknown>;
  relationshipJson: Array<Record<string, unknown>>;
}

interface TensionViewState {
  tensions: string[];
  jobToBeDone: string;
  raw: unknown;
}

interface RecipeViewState {
  attributes: NonNullable<AiTestRun['result']['attributes']>;
  tabName: string;
  brandName: string;
  formatName: string;
  combinationName: string;
}

interface WrittenViewState {
  text: string;
  tabName: string;
  brandName: string;
  formatName: string;
  combinationName: string;
}

interface VisualViewState {
  imageUrl: string;
  imageB64: string;
  mimeType: string;
  tabName: string;
  combinationName: string;
}

const RunHistory: React.FC<RunHistoryProps> = ({ runs, models, onRefresh }) => {
  const [openId, setOpenId] = useState<number | null>(null);
  const [artifact, setArtifact] = useState<string>('');
  const [jtbdView, setJtbdView] = useState<JtbdViewState | null>(null);
  const [tensionView, setTensionView] = useState<TensionViewState | null>(null);
  const [recipeView, setRecipeView] = useState<RecipeViewState | null>(null);
  const [writtenView, setWrittenView] = useState<WrittenViewState | null>(null);
  const [visualView, setVisualView] = useState<VisualViewState | null>(null);
  const [jobId, setJobId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const pending = runs.some(
      (run) => run.status === 'pending' || run.status === 'in_progress'
    );
    if (!pending) return undefined;
    const timer = window.setInterval(() => {
      void onRefresh();
    }, 8000);
    return () => window.clearInterval(timer);
  }, [runs, onRefresh]);

  useEffect(() => {
    if (openId == null) return;
    const run = runs.find((item) => item.id === openId);
    if (run?.generator_key === 'jtbd' && !jtbdView && (run.status === 'completed' || run.status === 'partial')) {
      void (async () => {
        setLoading(true);
        try {
          const latest = await getTesterRun(run.id);
          const data = await getTesterRunArtifact(latest.id);
          const intermediate = (data.intermediate_results || latest.result?.intermediate_results || {}) as Record<string, unknown>;
          setJtbdView({
            statements: data.jtbd_statements || latest.result?.jtbd_statements || [],
            intermediate,
            relationshipJson: (
              data.relationship_json
              || latest.result?.relationship_json
              || (intermediate.relationship_json as Array<Record<string, unknown>> | undefined)
              || []
            ),
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not load JTBD result');
        } finally {
          setLoading(false);
        }
      })();
    }
    if (run?.generator_key === 'tension' && !tensionView && (run.status === 'completed' || run.status === 'partial')) {
      void (async () => {
        setLoading(true);
        try {
          const latest = await getTesterRun(run.id);
          const data = await getTesterRunArtifact(latest.id);
          const raw = data.tensions || latest.result?.tensions || [];
          setTensionView({
            tensions: raw.map(tensionText).filter(Boolean),
            jobToBeDone: data.job_to_be_done || latest.result?.job_to_be_done || '',
            raw,
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not load tension result');
        } finally {
          setLoading(false);
        }
      })();
    }
    if (run?.generator_key === 'recipe_attributes' && !recipeView && (run.status === 'completed' || run.status === 'partial')) {
      void (async () => {
        setLoading(true);
        try {
          const latest = await getTesterRun(run.id);
          const data = await getTesterRunArtifact(latest.id);
          setRecipeView({
            attributes: data.attributes || latest.result?.attributes || [],
            tabName: data.tab_name || latest.result?.tab_name || '',
            brandName: data.brand_name || latest.result?.brand_name || '',
            formatName: data.format_name || latest.result?.format_name || '',
            combinationName: data.combination_name || latest.result?.combination_name || '',
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not load recipe result');
        } finally {
          setLoading(false);
        }
      })();
    }
    if (run?.generator_key === 'written_concept' && !writtenView && (run.status === 'completed' || run.status === 'partial')) {
      void (async () => {
        setLoading(true);
        try {
          const latest = await getTesterRun(run.id);
          const data = await getTesterRunArtifact(latest.id);
          setWrittenView({
            text: data.written_concept || data.content || latest.result?.written_concept || '',
            tabName: data.tab_name || latest.result?.tab_name || '',
            brandName: data.brand_name || latest.result?.brand_name || '',
            formatName: data.format_name || latest.result?.format_name || '',
            combinationName: data.combination_name || latest.result?.combination_name || '',
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not load written concept');
        } finally {
          setLoading(false);
        }
      })();
    }
    if (run && isVisualGenerator(run.generator_key) && !visualView && (run.status === 'completed' || run.status === 'partial')) {
      void (async () => {
        setLoading(true);
        try {
          const latest = await getTesterRun(run.id);
          const data = await getTesterRunArtifact(latest.id);
          setVisualView({
            imageUrl: data.image_url || latest.result?.image_url || '',
            imageB64: data.image_b64 || latest.result?.image_b64 || '',
            mimeType: data.mime_type || latest.result?.mime_type || 'image/png',
            tabName: data.tab_name || latest.result?.tab_name || '',
            combinationName: data.combination_name || latest.result?.combination_name || '',
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not load visual concept');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [runs, openId, jtbdView, tensionView, recipeView, writtenView, visualView]);

  const openRun = async (run: AiTestRun, chosenJobId?: string) => {
    setOpenId(run.id);
    setLoading(true);
    setError(null);
    try {
      const latest = await getTesterRun(run.id);
      if (latest.generator_key === 'jtbd') {
        if (latest.status === 'pending' || latest.status === 'in_progress') {
          setJtbdView(null);
          setTensionView(null);
          setRecipeView(null);
          setWrittenView(null);
          setVisualView(null);
          setArtifact('');
          setJobId('');
          return;
        }
        const data = await getTesterRunArtifact(latest.id);
        const intermediate = (data.intermediate_results || latest.result?.intermediate_results || {}) as Record<string, unknown>;
        const relationshipJson = (
          data.relationship_json
          || latest.result?.relationship_json
          || (intermediate.relationship_json as Array<Record<string, unknown>> | undefined)
          || []
        );
        setJtbdView({
          statements: data.jtbd_statements || latest.result?.jtbd_statements || [],
          intermediate,
          relationshipJson,
        });
        setTensionView(null);
        setRecipeView(null);
        setWrittenView(null);
        setVisualView(null);
        setArtifact('');
        setJobId('');
        return;
      }
      if (latest.generator_key === 'tension') {
        if (latest.status === 'pending' || latest.status === 'in_progress') {
          setJtbdView(null);
          setTensionView(null);
          setRecipeView(null);
          setWrittenView(null);
          setVisualView(null);
          setArtifact('');
          setJobId('');
          return;
        }
        const data = await getTesterRunArtifact(latest.id);
        const raw = data.tensions || latest.result?.tensions || [];
        setTensionView({
          tensions: raw.map(tensionText).filter(Boolean),
          jobToBeDone: data.job_to_be_done || latest.result?.job_to_be_done || '',
          raw,
        });
        setJtbdView(null);
        setRecipeView(null);
        setWrittenView(null);
        setVisualView(null);
        setArtifact('');
        setJobId('');
        return;
      }
      if (latest.generator_key === 'recipe_attributes') {
        if (latest.status === 'pending' || latest.status === 'in_progress') {
          setJtbdView(null);
          setTensionView(null);
          setRecipeView(null);
          setWrittenView(null);
          setVisualView(null);
          setArtifact('');
          setJobId('');
          return;
        }
        const data = await getTesterRunArtifact(latest.id);
        setRecipeView({
          attributes: data.attributes || latest.result?.attributes || [],
          tabName: data.tab_name || latest.result?.tab_name || '',
          brandName: data.brand_name || latest.result?.brand_name || '',
          formatName: data.format_name || latest.result?.format_name || '',
          combinationName: data.combination_name || latest.result?.combination_name || '',
        });
        setJtbdView(null);
        setTensionView(null);
        setWrittenView(null);
        setVisualView(null);
        setArtifact('');
        setJobId('');
        return;
      }
      if (latest.generator_key === 'written_concept') {
        if (latest.status === 'pending' || latest.status === 'in_progress') {
          setJtbdView(null);
          setTensionView(null);
          setRecipeView(null);
          setWrittenView(null);
          setVisualView(null);
          setArtifact('');
          setJobId('');
          return;
        }
        const data = await getTesterRunArtifact(latest.id);
        setWrittenView({
          text: data.written_concept || data.content || latest.result?.written_concept || '',
          tabName: data.tab_name || latest.result?.tab_name || '',
          brandName: data.brand_name || latest.result?.brand_name || '',
          formatName: data.format_name || latest.result?.format_name || '',
          combinationName: data.combination_name || latest.result?.combination_name || '',
        });
        setJtbdView(null);
        setTensionView(null);
        setRecipeView(null);
        setVisualView(null);
        setArtifact('');
        setJobId('');
        return;
      }
      if (isVisualGenerator(latest.generator_key)) {
        if (latest.status === 'pending' || latest.status === 'in_progress') {
          setJtbdView(null);
          setTensionView(null);
          setRecipeView(null);
          setWrittenView(null);
          setVisualView(null);
          setArtifact('');
          setJobId('');
          return;
        }
        const data = await getTesterRunArtifact(latest.id);
        setVisualView({
          imageUrl: data.image_url || latest.result?.image_url || '',
          imageB64: data.image_b64 || latest.result?.image_b64 || '',
          mimeType: data.mime_type || latest.result?.mime_type || 'image/png',
          tabName: data.tab_name || latest.result?.tab_name || '',
          combinationName: data.combination_name || latest.result?.combination_name || '',
        });
        setJtbdView(null);
        setTensionView(null);
        setRecipeView(null);
        setWrittenView(null);
        setArtifact('');
        setJobId('');
        return;
      }
      setJtbdView(null);
      setTensionView(null);
      setRecipeView(null);
      setWrittenView(null);
      setVisualView(null);
      const nextJob =
        chosenJobId ||
        latest.pipeline_job_ids[0] ||
        latest.result?.jobs?.[0]?.job_id ||
        '';
      setJobId(nextJob);
      if (!nextJob) {
        setArtifact('');
        return;
      }
      const data = await getTesterRunArtifact(run.id, nextJob);
      setArtifact(data.content);
    } catch (err) {
      setArtifact('');
      setJtbdView(null);
      setTensionView(null);
      setRecipeView(null);
      setWrittenView(null);
      setVisualView(null);
      const message = err instanceof Error ? err.message : 'Report is not ready yet';
      setError(
        run.status === 'in_progress' && message.toLowerCase().includes('not available')
          ? 'Research is still running. Deep research jobs can take several minutes.'
          : message
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadRun = async (run: AiTestRun, chosenJobId?: string) => {
    setDownloadingId(run.id);
    setError(null);
    try {
      const latest = await getTesterRun(run.id);
      if (latest.generator_key === 'jtbd') {
        const data = await getTesterRunArtifact(latest.id);
        const statements = data.jtbd_statements || latest.result?.jtbd_statements || [];
        downloadTextFile(
          statements.map((statement, index) => `${index + 1}. ${statement}`).join('\n'),
          buildJtbdFilename(latest, 'statements'),
        );
        return;
      }
      if (latest.generator_key === 'tension') {
        const data = await getTesterRunArtifact(latest.id);
        const raw = data.tensions || latest.result?.tensions || [];
        const texts = raw.map(tensionText).filter(Boolean);
        downloadTextFile(
          texts.map((text, index) => `${index + 1}. ${text}`).join('\n'),
          buildTensionFilename(latest, 'tensions'),
        );
        return;
      }
      if (latest.generator_key === 'recipe_attributes') {
        const data = await getTesterRunArtifact(latest.id);
        const attributes = data.attributes || latest.result?.attributes || [];
        downloadTextFile(formatRecipeAttributes(attributes), buildRecipeFilename(latest, 'options'));
        return;
      }
      if (latest.generator_key === 'written_concept') {
        const data = await getTesterRunArtifact(latest.id);
        downloadTextFile(
          data.written_concept || data.content || latest.result?.written_concept || '',
          buildWrittenFilename(latest),
        );
        return;
      }
      if (isVisualGenerator(latest.generator_key)) {
        const data = await getTesterRunArtifact(latest.id);
        const imageB64 = data.image_b64 || latest.result?.image_b64 || '';
        const imageUrl = data.image_url || latest.result?.image_url || '';
        const mime = data.mime_type || latest.result?.mime_type || 'image/png';
        const filename = buildVisualFilename(latest, mime);
        if (imageB64) {
          downloadPngFromB64(imageB64, mime, filename);
          return;
        }
        if (imageUrl) {
          downloadImageUrl(imageUrl, filename);
          return;
        }
        setError('No image on this run yet.');
        return;
      }
      const jobs = latest.result?.jobs || run.result?.jobs || [];
      const nextJob =
        chosenJobId ||
        (openId === run.id ? jobId : '') ||
        latest.pipeline_job_ids[0] ||
        jobs[0]?.job_id ||
        '';
      if (!nextJob) {
        setError('No pipeline job on this run yet.');
        return;
      }
      const country = jobs.find((job) => job.job_id === nextJob)?.country;
      const cached =
        openId === run.id && jobId === nextJob && artifact ? artifact : '';
      const content = cached || (await getTesterRunArtifact(run.id, nextJob)).content;
      downloadTextFile(
        content,
        buildReportFilename(run, nextJob, country),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not download report';
      setError(
        run.status === 'in_progress' && message.toLowerCase().includes('not available')
          ? 'Research is still running. Deep research jobs can take several minutes.'
          : message
      );
      if (openId === run.id) {
        setArtifact('');
        setJtbdView(null);
        setTensionView(null);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadTensionJson = async (run: AiTestRun) => {
    setDownloadingId(run.id);
    setError(null);
    try {
      const latest = await getTesterRun(run.id);
      const data = await getTesterRunArtifact(latest.id);
      downloadJsonFile(data.tensions || latest.result?.tensions || [], buildTensionFilename(latest, 'json'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download tensions JSON');
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadRecipeJson = async (run: AiTestRun) => {
    setDownloadingId(run.id);
    setError(null);
    try {
      const latest = await getTesterRun(run.id);
      const data = await getTesterRunArtifact(latest.id);
      downloadJsonFile(
        data.attributes || latest.result?.attributes || [],
        buildRecipeFilename(latest, 'json'),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download recipe JSON');
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadRelationships = async (run: AiTestRun) => {
    setDownloadingId(run.id);
    setError(null);
    try {
      const latest = await getTesterRun(run.id);
      const data = await getTesterRunArtifact(latest.id);
      const intermediate = (data.intermediate_results || latest.result?.intermediate_results || {}) as Record<string, unknown>;
      const relationshipJson =
        data.relationship_json
        || latest.result?.relationship_json
        || intermediate.relationship_json
        || [];
      downloadJsonFile(relationshipJson, buildJtbdFilename(latest, 'step5'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download relationships');
    } finally {
      setDownloadingId(null);
    }
  };

  if (runs.length === 0) {
    return <p className="text-sm text-[#8A8A8A]">No test runs yet.</p>;
  }

  return (
    <div className="space-y-3">
      {runs.map((run) => {
        const jobs = run.result?.jobs || [];
        const usage = usageSummary(run, models);
        const modelLabel = runModelLabel(run);
        return (
          <div key={run.id} className="rounded-md border border-[#E8E8E8] p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[#0C0B0B]">
                  #{run.id} · {run.project_name}
                </p>
                <p className="text-[12px] text-[#8A8A8A]">
                  {run.status}
                  {modelLabel ? ` · ${modelLabel}` : ''}
                  {usage ? ` · ${usage}` : ''}
                  {run.created_by_email ? ` · ${run.created_by_email}` : ''}
                </p>
                {run.result?.override_requested && run.status !== 'pending' && run.status !== 'in_progress' && (
                  <p
                    className={`text-[12px] ${
                      run.result.applied_overrides ? 'text-emerald-700' : 'text-amber-700'
                    }`}
                  >
                    {run.result.applied_overrides
                      ? `Override applied (${
                          typeof run.result.applied_overrides.prompt === 'string'
                            ? run.result.applied_overrides.prompt
                            : run.result.applied_overrides.prompt
                              ? 'custom prompts'
                              : 'default prompt'
                        })`
                      : 'Override requested but not confirmed by the pipeline'}
                  </p>
                )}
                {run.result?.prompt_preview && (
                  <p className="text-[11px] text-[#626262] font-mono">
                    Sent to pipeline ({run.result.prompt_source || 'unknown'}):{' '}
                    {run.result.prompt_preview.length > 120
                      ? `${run.result.prompt_preview.slice(0, 120)}…`
                      : run.result.prompt_preview}
                  </p>
                )}
                {run.result?.prompt_sources && Object.keys(run.result.prompt_sources).length > 0 && (
                  <p className="text-[11px] text-[#626262]">
                    Prompt sources:{' '}
                    {Object.entries(run.result.prompt_sources)
                      .map(([step, source]) => `${step}=${source}`)
                      .join(', ')}
                  </p>
                )}
                {run.result?.job_to_be_done && (
                  <p className="text-[11px] text-[#626262]">
                    Job: {run.result.job_to_be_done.length > 160
                      ? `${run.result.job_to_be_done.slice(0, 160)}…`
                      : run.result.job_to_be_done}
                  </p>
                )}
                {run.result?.source_of_truth_source && (
                  <p className="text-[11px] text-[#626262]">
                    Source of truth: {run.result.source_of_truth_source}
                  </p>
                )}
                {run.result?.pipeline_url?.includes('azurewebsites.net') && (
                  <p className="text-[11px] text-amber-700">
                    Warning: this run used the deployed Azure pipeline, which does not apply
                    prompt overrides. Point RESEARCH_PIPELINE_URL at localhost:7071 for local testing.
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {run.generator_key === 'jtbd' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void downloadRun(run)}
                      disabled={downloadingId === run.id || run.status === 'pending' || run.status === 'in_progress'}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                    >
                      {downloadingId === run.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      Download statements
                    </button>
                    <button
                      type="button"
                      onClick={() => void downloadRelationships(run)}
                      disabled={downloadingId === run.id || run.status === 'pending' || run.status === 'in_progress'}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                    >
                      Download step 5 JSON
                    </button>
                  </>
                ) : run.generator_key === 'tension' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void downloadRun(run)}
                      disabled={downloadingId === run.id || run.status === 'pending' || run.status === 'in_progress'}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                    >
                      {downloadingId === run.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      Download tensions
                    </button>
                    <button
                      type="button"
                      onClick={() => void downloadTensionJson(run)}
                      disabled={downloadingId === run.id || run.status === 'pending' || run.status === 'in_progress'}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                    >
                      Download JSON
                    </button>
                  </>
                ) : run.generator_key === 'recipe_attributes' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void downloadRun(run)}
                      disabled={downloadingId === run.id || run.status === 'pending' || run.status === 'in_progress'}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                    >
                      {downloadingId === run.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      Download options
                    </button>
                    <button
                      type="button"
                      onClick={() => void downloadRecipeJson(run)}
                      disabled={downloadingId === run.id || run.status === 'pending' || run.status === 'in_progress'}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                    >
                      Download JSON
                    </button>
                  </>
                ) : run.generator_key === 'written_concept' ? (
                  <button
                    type="button"
                    onClick={() => void downloadRun(run)}
                    disabled={downloadingId === run.id || run.status === 'pending' || run.status === 'in_progress'}
                    className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                  >
                    {downloadingId === run.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                    Download .txt
                  </button>
                ) : isVisualGenerator(run.generator_key) ? (
                  <button
                    type="button"
                    onClick={() => void downloadRun(run)}
                    disabled={downloadingId === run.id || run.status === 'pending' || run.status === 'in_progress'}
                    className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                  >
                    {downloadingId === run.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                    Download .png
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void downloadRun(run)}
                    disabled={downloadingId === run.id || run.status === 'pending'}
                    className="inline-flex h-8 items-center gap-1 rounded-md border border-[#D1D1D1] px-3 text-[12px] disabled:opacity-50"
                  >
                    {downloadingId === run.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                    Download .txt
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void openRun(run)}
                  className="h-8 px-3 rounded-md border border-[#D1D1D1] text-[12px]"
                >
                  View
                </button>
              </div>
            </div>
            {run.error && <p className="mt-2 text-[12px] text-red-600">{run.error}</p>}
            {openId === run.id && (
              <div className="mt-3 space-y-2">
                {jobs.length > 1 && (
                  <select
                    value={jobId}
                    onChange={(e) => void openRun(run, e.target.value)}
                    className="w-full rounded-md border border-[#D1D1D1] px-2 py-1.5 text-sm"
                  >
                    {jobs.map((job) => (
                      <option key={job.job_id} value={job.job_id}>
                        {job.country || job.job_id} ({job.status})
                      </option>
                    ))}
                  </select>
                )}
                {loading && (
                  <div className="flex items-center gap-2 text-[12px] text-[#8A8A8A]">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading report…
                  </div>
                )}
                {error && <p className="text-[12px] text-red-600">{error}</p>}
                {run.generator_key === 'jtbd' && (run.status === 'pending' || run.status === 'in_progress') && (
                  <p className="text-[12px] text-[#626262]">
                    Generation is still running. JTBD is six steps: five sequential model
                    calls, then a code transform for the step 5 JSON. This often takes several minutes.
                  </p>
                )}
                {run.generator_key === 'tension' && (run.status === 'pending' || run.status === 'in_progress') && (
                  <p className="text-[12px] text-[#626262]">
                    Generation is still running. Tension is a single model call and usually finishes within a minute.
                  </p>
                )}
                {run.generator_key === 'recipe_attributes' && (run.status === 'pending' || run.status === 'in_progress') && (
                  <p className="text-[12px] text-[#626262]">
                    Generation is still running. Each selected attribute is a concurrent model call.
                  </p>
                )}
                {run.generator_key === 'written_concept' && (run.status === 'pending' || run.status === 'in_progress') && (
                  <p className="text-[12px] text-[#626262]">
                    Generation is still running. Written concept is a single model call.
                  </p>
                )}
                {isVisualGenerator(run.generator_key) && (run.status === 'pending' || run.status === 'in_progress') && (
                  <p className="text-[12px] text-[#626262]">
                    Generation is still running. Image models usually take longer than text.
                  </p>
                )}
                {run.generator_key === 'written_concept' && writtenView && (
                  <div className="space-y-3">
                    <p className="text-[12px] text-[#626262]">
                      {writtenView.tabName && <>Card: <span className="text-[#0C0B0B]">{writtenView.tabName}</span></>}
                      {writtenView.brandName && <> · Brand: <span className="text-[#0C0B0B]">{writtenView.brandName}</span></>}
                      {writtenView.formatName && <> · Format: <span className="text-[#0C0B0B]">{writtenView.formatName}</span></>}
                      {writtenView.combinationName && <> · Combination: <span className="text-[#0C0B0B]">{writtenView.combinationName}</span></>}
                    </p>
                    <pre className="max-h-80 overflow-auto rounded-md bg-[#F7F7F7] p-3 text-[12px] whitespace-pre-wrap">
                      {writtenView.text}
                    </pre>
                    <TokenUsageBlock run={run} models={models} />
                  </div>
                )}
                {isVisualGenerator(run.generator_key) && visualView && (
                  <div className="space-y-3">
                    <p className="text-[12px] text-[#626262]">
                      {visualView.tabName && <>Card: <span className="text-[#0C0B0B]">{visualView.tabName}</span></>}
                      {visualView.combinationName && <> · Combination: <span className="text-[#0C0B0B]">{visualView.combinationName}</span></>}
                    </p>
                    {(() => {
                      const src = visualSrc(visualView.imageUrl, visualView.imageB64, visualView.mimeType);
                      if (!src) {
                        return <p className="text-[12px] text-[#8A8A8A]">No image on this run.</p>;
                      }
                      return (
                        <img
                          src={src}
                          alt={`Visual concept from run ${run.id}`}
                          className="max-h-96 w-full rounded-md border border-[#E8E8E8] object-contain bg-[#F7F7F7]"
                        />
                      );
                    })()}
                    <TokenUsageBlock run={run} models={models} />
                  </div>
                )}
                {run.generator_key === 'recipe_attributes' && recipeView && (
                  <div className="space-y-3">
                    <p className="text-[12px] text-[#626262]">
                      {recipeView.tabName && <>Card: <span className="text-[#0C0B0B]">{recipeView.tabName}</span></>}
                      {recipeView.brandName && <> · Brand: <span className="text-[#0C0B0B]">{recipeView.brandName}</span></>}
                      {recipeView.formatName && <> · Format: <span className="text-[#0C0B0B]">{recipeView.formatName}</span></>}
                      {recipeView.combinationName && <> · Combination: <span className="text-[#0C0B0B]">{recipeView.combinationName}</span></>}
                    </p>
                    {recipeView.attributes.map((block) => (
                      <div key={block.name || 'attr'} className="rounded-md border border-[#E8E8E8] p-3">
                        <p className="text-[12px] font-medium text-[#0C0B0B] mb-1">{block.name}</p>
                        {block.error && <p className="text-[12px] text-red-600">{block.error}</p>}
                        {(['established', 'emerging', 'breakthrough'] as const).map((bucket) => {
                          const items = block.options?.[bucket] || [];
                          if (!items.length) return null;
                          return (
                            <div key={bucket} className="mt-2">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-[#8A8A8A]">{bucket}</p>
                              <ul className="mt-1 list-disc pl-5 space-y-0.5 text-[12px] text-[#0C0B0B]">
                                {items.map((item, index) => (
                                  <li key={`${bucket}-${index}`}>{optionText(item)}</li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    <TokenUsageBlock run={run} models={models} />
                  </div>
                )}
                {run.generator_key === 'tension' && tensionView && (
                  <div className="space-y-3">
                    {tensionView.jobToBeDone && (
                      <p className="text-[12px] text-[#626262]">
                        Job: <span className="text-[#0C0B0B]">{tensionView.jobToBeDone}</span>
                      </p>
                    )}
                    <ol className="list-decimal pl-5 space-y-1 text-[12px] text-[#0C0B0B]">
                      {tensionView.tensions.map((text, index) => (
                        <li key={`${index}-${text.slice(0, 24)}`}>{text}</li>
                      ))}
                    </ol>
                    <TokenUsageBlock run={run} models={models} />
                  </div>
                )}
                {run.generator_key === 'jtbd' && jtbdView && (
                  <div className="space-y-3">
                    <ol className="list-decimal pl-5 space-y-1 text-[12px] text-[#0C0B0B]">
                      {jtbdView.statements.map((statement, index) => (
                        <li key={`${index}-${statement.slice(0, 24)}`}>{statement}</li>
                      ))}
                    </ol>
                    <TokenUsageBlock run={run} models={models} />
                    <details className="rounded-md bg-[#F7F7F7] p-3">
                      <summary className="cursor-pointer text-[12px] font-medium">Intermediate results</summary>
                      <pre className="mt-2 max-h-64 overflow-auto text-[11px] whitespace-pre-wrap">
                        {JSON.stringify(jtbdView.intermediate, null, 2)}
                      </pre>
                    </details>
                    <details className="rounded-md bg-[#F7F7F7] p-3">
                      <summary className="cursor-pointer text-[12px] font-medium">Step 5 JSON</summary>
                      <pre className="mt-2 max-h-64 overflow-auto text-[11px] whitespace-pre-wrap">
                        {JSON.stringify(jtbdView.relationshipJson, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
                {artifact && (
                  <div className="space-y-2">
                    <TokenUsageBlock run={run} models={models} />
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => void downloadRun(run, jobId)}
                        disabled={downloadingId === run.id}
                        className="inline-flex items-center gap-1 rounded-md border border-[#D1D1D1] px-2 py-1 text-[11px] disabled:opacity-50"
                      >
                        {downloadingId === run.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Download className="h-3 w-3" />
                        )}
                        Download .txt
                      </button>
                    </div>
                    <pre className="max-h-80 overflow-auto rounded-md bg-[#F7F7F7] p-3 text-[12px] whitespace-pre-wrap">
                      {artifact}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RunHistory;
